import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { CheckCircle2, Film, Image as ImageIcon, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function imgSrc(v) {
  if (!v) return null;
  if (v.startsWith("http") || v.startsWith("/")) return v;
  return `${BACKEND}/api/files/${v}`;
}

const STATUS_TABS = [
  { id: "pending", label: "En attente" },
  { id: "approved", label: "Approuvés" },
  { id: "rejected", label: "Rejetés" },
];

export default function AdminMedia() {
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState(null);
  const [counts, setCounts] = useState({});
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async (s = status) => {
    setItems(null);
    try {
      const { data } = await api.get(`/admin/media?status=${s}`);
      setItems(data.items || []);
      setCounts(data.counts || {});
    } catch (e) { toast.error(formatApiError(e)); setItems([]); }
  };
  // eslint-disable-next-line
  useEffect(() => { load(status); }, [status]);

  const moderate = async (id, action, rejectReason = "") => {
    setBusy(true);
    try {
      await api.patch(`/admin/media/${id}`, { action, reason: rejectReason });
      toast.success(action === "approve" ? "Média approuvé — visible publiquement" : "Média rejeté — masqué du public");
      setRejecting(null); setReason("");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-media-page">
      <div className="card-mc p-5 bg-[hsl(var(--muted))]/40 mb-6">
        <p className="overline mb-2">Modération des contenus · Photos & vidéos</p>
        <p className="text-sm">Validation a priori : chaque nouvelle photo/vidéo ajoutée par un artiste est invisible du public tant qu'elle n'est pas approuvée ici.</p>
      </div>

      <div className="flex gap-1 border-b border-border mb-6">
        {STATUS_TABS.map((t) => (
          <button key={t.id} data-testid={`media-tab-${t.id}`} onClick={() => setStatus(t.id)}
            className={`px-4 py-2.5 text-sm font-bold border-b-2 transition-colors ${status === t.id ? "border-foreground text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {t.label}{counts[t.id] ? ` (${counts[t.id]})` : ""}
          </button>
        ))}
      </div>

      {items === null ? (
        <div className="py-12 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="card-mc p-12 text-center text-muted-foreground" data-testid="media-empty">
          {status === "pending" ? "Aucun média en attente de validation. 🎉" : "Aucun média dans cette catégorie."}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="media-grid">
          {items.map((m) => (
            <div key={m.id} className="card-mc overflow-hidden" data-testid={`media-item-${m.id}`}>
              {m.media_type === "photo" ? (
                <a href={imgSrc(m.value)} target="_blank" rel="noreferrer" className="block aspect-[4/3] bg-muted">
                  <img src={imgSrc(m.value)} alt="" className="w-full h-full object-cover" />
                </a>
              ) : (
                <div className="aspect-[4/3] bg-muted flex flex-col items-center justify-center p-4 text-center">
                  <Film className="w-8 h-8 text-muted-foreground mb-2" />
                  <a href={m.value} target="_blank" rel="noreferrer" className="text-xs font-mono underline break-all line-clamp-3" data-testid={`media-video-link-${m.id}`}>{m.value}</a>
                </div>
              )}
              <div className="p-4">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-display font-bold text-sm truncate">{m.artist_name}</p>
                  <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1">
                    {m.media_type === "photo" ? <ImageIcon className="w-3 h-3" /> : <Film className="w-3 h-3" />} {m.media_type === "photo" ? "Photo" : "Vidéo"}
                  </span>
                </div>
                <p className="text-[10px] font-mono text-muted-foreground mt-0.5">{new Date(m.created_at).toLocaleString("fr-FR")}</p>
                {m.status === "rejected" && m.reason && <p className="text-xs text-destructive mt-1">Motif : {m.reason}</p>}

                {m.status !== "approved" && (
                  <button data-testid={`approve-media-${m.id}`} disabled={busy} onClick={() => moderate(m.id, "approve")} className="btn-secondary text-xs w-full mt-3">
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Approuver
                  </button>
                )}
                {m.status !== "rejected" && (
                  rejecting === m.id ? (
                    <div className="mt-2" data-testid={`reject-form-${m.id}`}>
                      <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Motif du rejet (optionnel)"
                        className="w-full border border-border px-2.5 py-1.5 rounded-sm bg-card text-xs" />
                      <div className="flex gap-2 mt-1.5">
                        <button data-testid={`cancel-reject-${m.id}`} onClick={() => { setRejecting(null); setReason(""); }} className="btn-ghost text-xs flex-1">Annuler</button>
                        <button data-testid={`confirm-reject-${m.id}`} disabled={busy} onClick={() => moderate(m.id, "reject", reason)} className="btn-primary text-xs flex-1 bg-destructive">Confirmer le rejet</button>
                      </div>
                    </div>
                  ) : (
                    <button data-testid={`reject-media-${m.id}`} disabled={busy} onClick={() => { setRejecting(m.id); setReason(""); }} className="btn-outline text-xs w-full mt-2 text-destructive border-destructive">
                      <XCircle className="w-3.5 h-3.5 mr-1.5" /> Rejeter
                    </button>
                  )
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
